import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CompanyLogoSvg, { COMPANY_LOGO_SIZE } from "./CompanyLogoSvg";
import WebAdCrosswordBoard from "./WebAdCrosswordBoard";
import WebAdJigsawBoard from "./WebAdJigsawBoard";
import WebAdTutorBoard from "./WebAdTutorBoard";
import WebAdWordWheelBoard from "./WebAdWordWheelBoard";
import { WEB_AD_TUTOR_STORY_TITLE } from "./webAdShots";

const FONT = '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';

const COLORS = {
    ink: "#0F172A",
    muted: "#475569",
    soft: "#94A3B8",
    cream: "#F8FAFC",
    line: "#CBD5E1",
    accent: "#0EA5E9",
    green: "#3BA666",
    board: "#E2E8F0",
    piece: "#FFFFFF",
    tray: "#1E293B",
};

function BrandMark({ size = 56 }) {
    const scale = size / COMPANY_LOGO_SIZE;
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: Math.round(size * 0.18),
                overflow: "hidden",
                flexShrink: 0,
                boxShadow: "0 8px 24px rgba(15,23,42,0.16)",
            }}
        >
            <Box
                sx={{
                    width: COMPANY_LOGO_SIZE,
                    height: COMPANY_LOGO_SIZE,
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    lineHeight: 0,
                }}
            >
                <CompanyLogoSvg />
            </Box>
        </Box>
    );
}

function CopyBlock({ headline, sub, align = "left", headlineSize = 42, subSize = 20 }) {
    return (
        <Box sx={{ textAlign: align, maxWidth: "100%" }}>
            <Typography
                component="div"
                sx={{
                    fontFamily: FONT,
                    fontWeight: 800,
                    fontSize: headlineSize,
                    letterSpacing: "-0.03em",
                    color: COLORS.ink,
                    lineHeight: 1.12,
                }}
            >
                {headline}
            </Typography>
            <Typography
                component="div"
                sx={{
                    mt: 1,
                    fontFamily: FONT,
                    fontWeight: 600,
                    fontSize: subSize,
                    color: COLORS.muted,
                    lineHeight: 1.3,
                }}
            >
                {sub}
            </Typography>
        </Box>
    );
}

function FrameShell({ width, height, children, frameRef }) {
    return (
        <Box
            ref={frameRef}
            data-web-ad-frame="1"
            sx={{
                width,
                height,
                position: "relative",
                overflow: "hidden",
                boxSizing: "border-box",
                background: `linear-gradient(145deg, ${COLORS.cream} 0%, #E8EEF5 48%, #D6E0EC 100%)`,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(ellipse 75% 60% at 70% 40%, rgba(255,255,255,0.7) 0%, transparent 68%)",
                    pointerEvents: "none",
                }}
            />
            <Box sx={{ position: "relative", zIndex: 1, width: "100%", height: "100%" }}>
                {children}
            </Box>
        </Box>
    );
}

/** Live jigsaw board from WEB_AD_JIGSAW_STORY_ID (kept for call-site clarity). */
function JigsawBoard(props) {
    return <WebAdJigsawBoard {...props} />;
}

function CrosswordBoard(props) {
    return <WebAdCrosswordBoard {...props} />;
}

function TutorPanel({ variant }) {
    return <WebAdTutorBoard variant={variant} />;
}

export function WebAdShotArt({ shot, width, height, frameRef, exportLite = false }) {
    const isSquare = shot.asset === "square";
    const pad = isSquare ? 56 : 48;

    if (shot.id === "jigsaw" && !isSquare) {
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: `${pad}px`,
                        gap: 4,
                    }}
                >
                    <Box sx={{ flex: "0 1 420px" }}>
                        <BrandMark size={64} />
                        <Box sx={{ mt: 2.5 }}>
                            <CopyBlock headline={shot.headline} sub={shot.sub} headlineSize={40} subSize={20} />
                        </Box>
                    </Box>
                    <JigsawBoard size={340} showTray trayBeside={false} exportLite={exportLite} />
                </Box>
            </FrameShell>
        );
    }

    if (shot.id === "jigsaw" && isSquare) {
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        px: `${pad}px`,
                        gap: 3,
                    }}
                >
                    <CopyBlock
                        headline={shot.headline}
                        sub={shot.sub}
                        align="center"
                        headlineSize={44}
                        subSize={22}
                    />
                    <JigsawBoard size={620} showTray trayBeside exportLite={exportLite} />
                </Box>
            </FrameShell>
        );
    }

    if (shot.id === "word-wheel" && !isSquare) {
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: `${pad}px`,
                        gap: 3,
                    }}
                >
                    <Box sx={{ flex: "0 1 380px" }}>
                        <BrandMark size={60} />
                        <Box sx={{ mt: 2.5 }}>
                            <CopyBlock headline={shot.headline} sub={shot.sub} headlineSize={38} subSize={20} />
                        </Box>
                    </Box>
                    <WebAdWordWheelBoard boardSize={280} wheelSize={250} layout="side" />
                </Box>
            </FrameShell>
        );
    }

    if (shot.id === "word-wheel" && isSquare) {
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        px: `${pad}px`,
                        gap: 3,
                    }}
                >
                    <CopyBlock
                        headline={shot.headline}
                        sub={shot.sub}
                        align="center"
                        headlineSize={42}
                        subSize={22}
                    />
                    <WebAdWordWheelBoard boardSize={320} wheelSize={400} layout="stack" />
                </Box>
            </FrameShell>
        );
    }

    if (shot.id === "crossword" && !isSquare) {
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: `${pad}px`,
                        gap: 3,
                    }}
                >
                    <Box sx={{ flex: "0 1 400px" }}>
                        <BrandMark size={60} />
                        <Box sx={{ mt: 2.5 }}>
                            <CopyBlock headline={shot.headline} sub={shot.sub} headlineSize={38} subSize={20} />
                        </Box>
                    </Box>
                    <CrosswordBoard size={360} showInput />
                </Box>
            </FrameShell>
        );
    }

    if (shot.id === "crossword" && isSquare) {
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        px: `${pad}px`,
                        gap: 2.5,
                    }}
                >
                    <CopyBlock
                        headline={shot.headline}
                        sub={shot.sub}
                        align="center"
                        headlineSize={42}
                        subSize={22}
                    />
                    <CrosswordBoard size={680} showInput />
                </Box>
            </FrameShell>
        );
    }

    if (shot.id.startsWith("tutor-")) {
        const variant =
            shot.id === "tutor-content" ? "content" : shot.id === "tutor-make" ? "make" : "print";
        return (
            <FrameShell width={width} height={height} frameRef={frameRef}>
                <Box
                    sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: `${pad}px`,
                        gap: 4,
                    }}
                >
                    <Box sx={{ flex: "0 1 440px" }}>
                        <BrandMark size={60} />
                        <Box sx={{ mt: 2.5 }}>
                            <CopyBlock headline={shot.headline} sub={shot.sub} headlineSize={40} subSize={22} />
                        </Box>
                        <Typography
                            sx={{
                                mt: 2,
                                fontFamily: FONT,
                                fontWeight: 700,
                                fontSize: 16,
                                color: COLORS.accent,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                            }}
                        >
                            Tutor · {WEB_AD_TUTOR_STORY_TITLE}
                        </Typography>
                    </Box>
                    <TutorPanel variant={variant} />
                </Box>
            </FrameShell>
        );
    }

    return (
        <FrameShell width={width} height={height} frameRef={frameRef}>
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CopyBlock headline={shot.headline} sub={shot.sub} align="center" />
            </Box>
        </FrameShell>
    );
}
